import {
	AssignmentReason,
	ConsentChannel,
	ConsentPurpose,
	ConsentStatus,
	ContactPointLabel,
	ContactPointType,
	CUSTOMER_MAX_CONTACT_POINTS,
	CustomerChannel,
	CustomerStatus,
	CustomerType,
} from '@today/types'
import { z } from 'zod'

// Customer model v2 request schemas (TO-418). The wire contract is snake_case;
// `tenantId` is never accepted — it comes from the DB-resolved principal. Every
// object is `.strict()` so unknown properties (e.g. the retired `full_name`)
// are refused with 422 rather than ignored.

const firstNameSchema = z.string().trim().min(1).max(100)

/** Optional last name: trimmed, ≤ 100 characters; an empty value is stored as null. */
const lastNameSchema = z
	.string()
	.trim()
	.max(100)
	.nullish()
	.transform((value) => (value ? value : null))

const tagSchema = z
	.string()
	.trim()
	.min(1)
	.max(50)
	.refine((tag) => !tag.includes(','), { message: 'a tag cannot contain a comma' })

/** ≤ 20 tags, lowercased and de-duplicated keeping first-occurrence order. */
export const customerTagsSchema = z
	.array(tagSchema)
	.max(20)
	.transform((tags) => [...new Set(tags.map((tag) => tag.toLowerCase()))])

/** BCP-47 language (+ optional region), e.g. `es`, `gl-ES`, `ast`. */
export const customerLocaleSchema = z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, 'locale must look like es-ES')

/** Partner/CDP identifier — also the upsert path segment. */
export const customerExternalIdSchema = z
	.string()
	.regex(/^[A-Za-z0-9._:-]{1,128}$/, 'external id must be 1–128 chars of A-Z a-z 0-9 . _ : -')

const statusPartnerLabelSchema = z.string().trim().min(1).max(100).nullable()

const EVIDENCE_MAX_BYTES = 4096

// ─── Contact points ──────────────────────────────────────────────────────────

export const contactPointInputSchema = z
	.object({
		type: z.enum(ContactPointType),
		value: z.string().trim().min(1).max(320),
		label: z.enum(ContactPointLabel).default(ContactPointLabel.PERSONAL),
		is_primary: z.boolean().optional(),
	})
	.strict()

/** `type` is accepted only so an attempted change maps to `CUSTOMER_IMMUTABLE_FIELD`. */
export const contactPointPatchSchema = z
	.object({
		type: z.unknown().optional(),
		value: z.string().trim().min(1).max(320).optional(),
		label: z.enum(ContactPointLabel).optional(),
		is_primary: z.boolean().optional(),
		verified: z.boolean().optional(),
		invalidated: z.boolean().optional(),
	})
	.strict()

export const contactPointOrderSchema = z
	.object({
		type: z.enum(ContactPointType),
		contact_point_ids: z.array(z.uuid()).min(1).max(CUSTOMER_MAX_CONTACT_POINTS),
	})
	.strict()

export const customerLookupQuerySchema = z.object({
	type: z.enum(ContactPointType),
	value: z.string().min(1).max(320),
})

// ─── Consents ────────────────────────────────────────────────────────────────

export const consentInputSchema = z
	.object({
		channel: z.enum(ConsentChannel),
		purpose: z.enum(ConsentPurpose),
		status: z.enum(ConsentStatus),
		contact_point_id: z.uuid().optional(),
		legal_text_id: z.string().min(1).max(100).optional(),
		legal_text_version: z.string().min(1).max(50).optional(),
		evidence: z
			.record(z.string(), z.unknown())
			.refine((value) => Buffer.byteLength(JSON.stringify(value), 'utf8') <= EVIDENCE_MAX_BYTES, {
				message: `evidence must serialize to at most ${EVIDENCE_MAX_BYTES} bytes`,
			})
			.optional(),
		effective_at: z.iso
			.datetime({ offset: true })
			.transform((value) => new Date(value))
			.optional(),
	})
	.strict()

// ─── Customer ────────────────────────────────────────────────────────────────

const profileShape = {
	first_name: firstNameSchema,
	last_name_1: lastNameSchema,
	last_name_2: lastNameSchema,
	type: z.enum(CustomerType).default(CustomerType.LEAD),
	status: z.enum(CustomerStatus).default(CustomerStatus.ACTIVE),
	status_partner_label: statusPartnerLabelSchema.optional().transform((value) => value ?? null),
	preferred_channel: z
		.enum(CustomerChannel)
		.nullish()
		.transform((value) => value ?? null),
	preferred_locale: customerLocaleSchema.nullish().transform((value) => value ?? null),
	tags: customerTagsSchema.default([]),
	org_unit_id: z.uuid(),
	assigned_user_id: z.uuid(),
	contact_points: z.array(contactPointInputSchema).min(1).max(CUSTOMER_MAX_CONTACT_POINTS),
}

export const customerCreateSchema = z
	.object({
		...profileShape,
		// TO-388: optional on create — omitted, the unit comes from the caller's scope.
		org_unit_id: z.uuid().optional(),
		external_id: customerExternalIdSchema.optional(),
		consents: z.array(consentInputSchema).max(10).optional(),
	})
	.strict()

/** Upsert by external id: the create body without `external_id`/`consents`, plus `source_ref`. */
export const customerUpsertSchema = z
	.object({ ...profileShape, source_ref: z.string().min(1).max(255).optional() })
	.strict()

/**
 * PATCH surface. Assignment, contact points and consents have their own
 * endpoints; they are accepted here only so the domain can refuse them with
 * `CUSTOMER_IMMUTABLE_FIELD` instead of a generic schema error.
 */
export const customerPatchSchema = z
	.object({
		first_name: firstNameSchema.optional(),
		last_name_1: z.string().trim().max(100).nullable().optional(),
		last_name_2: z.string().trim().max(100).nullable().optional(),
		type: z.enum(CustomerType).optional(),
		status: z.enum(CustomerStatus).optional(),
		status_partner_label: statusPartnerLabelSchema.optional(),
		preferred_channel: z.enum(CustomerChannel).nullable().optional(),
		preferred_locale: customerLocaleSchema.nullable().optional(),
		tags: customerTagsSchema.optional(),
		external_id: customerExternalIdSchema.nullable().optional(),
		org_unit_id: z.unknown().optional(),
		assigned_user_id: z.unknown().optional(),
		contact_points: z.unknown().optional(),
		consents: z.unknown().optional(),
	})
	.strict()

// ─── Assignment, notes ───────────────────────────────────────────────────────

/** Client-assignable reasons only; `initial` and `import_sync` are server-assigned. */
export const CLIENT_ASSIGNMENT_REASONS = [
	AssignmentReason.MANUAL_REASSIGN,
	AssignmentReason.USER_OFFBOARDED,
	AssignmentReason.UNIT_RESTRUCTURE,
] as const

export const assignmentInputSchema = z
	.object({
		org_unit_id: z.uuid(),
		assigned_user_id: z.uuid(),
		reason: z.enum(CLIENT_ASSIGNMENT_REASONS),
	})
	.strict()

export const noteInputSchema = z.object({ body: z.string().trim().min(1).max(5000) }).strict()

export type ContactPointInput = z.infer<typeof contactPointInputSchema>
export type ContactPointPatchInput = z.infer<typeof contactPointPatchSchema>
export type ContactPointOrderInput = z.infer<typeof contactPointOrderSchema>
export type ConsentInput = z.infer<typeof consentInputSchema>
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>
export type CustomerUpsertInput = z.infer<typeof customerUpsertSchema>
export type CustomerPatchInput = z.infer<typeof customerPatchSchema>
export type AssignmentInput = z.infer<typeof assignmentInputSchema>
export type NoteInput = z.infer<typeof noteInputSchema>
